import React from "react";
import styled, { useTheme } from "styled-components";
import { tokens } from "../../activity-logs/styles";
import { IOS } from "../../activity-logs/labels";
import { formatCompact, formatCurrency } from "../../creditos/dashboardData";
import { Num } from "../components/Motion";

/**
 * Fluxo da receita até a margem tributável: o que vira crédito (verde) e o que é
 * tributado (vermelho). As três colunas fecham sem rateio inventado: a margem bruta é
 * a sobra das duas receitas, e só ela se divide entre despesas e margem tributável.
 * As fitas ficam em SVG esticado; nós e rótulos em HTML, para o texto não deformar.
 */

const HEIGHT = 430;
const NODE_GAP = 14;
const OUT_GAP = 44;
const X = [0, 400, 760];
const MIN_NODE = 3;

const Wrap = styled.div`
  overflow-x: auto;
  scrollbar-width: thin;
  margin-top: 14px;
`;

const Canvas = styled.div`
  svg path { transition: d 420ms cubic-bezier(0.22, 0.8, 0.24, 1); }
  > :not(svg) { transition: top 420ms cubic-bezier(0.22, 0.8, 0.24, 1), height 420ms cubic-bezier(0.22, 0.8, 0.24, 1); }
  @media (prefers-reduced-motion: reduce) { svg path, > :not(svg) { transition: none; } }

  position: relative;
  min-width: 620px;
  height: ${HEIGHT}px;

  svg { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
`;

const Node = styled.div`
  position: absolute;
  width: 4px;
  min-height: ${MIN_NODE}px;
  border-radius: 2px;
`;

const Label = styled.div`
  position: absolute;
  padding-left: 12px;
  transform: translateY(-50%);
  white-space: nowrap;
  font-size: 0.78em;
  line-height: 1.25;
  color: ${({ theme }) => tokens.label2(theme)};
  pointer-events: none;

  &.before { padding: 0 8px 0 0; text-align: right; transform: translate(-100%, -50%); }

  b { display: block; font-size: 1.16em; font-weight: 600; letter-spacing: -0.02em; color: ${({ theme }) => theme.text}; }
`;

const ribbon = (x0, y0, h0, x1, y1, h1) => {
  const mid = (x0 + x1) / 2;
  return `M${x0},${y0} C${mid},${y0} ${mid},${y1} ${x1},${y1} L${x1},${y1 + h1} C${mid},${y1 + h1} ${mid},${y0 + h0} ${x0},${y0 + h0} Z`;
};

export default function MarginFlow({ flow }) {
  const theme = useTheme();
  const positive = (v) => Math.max(0, v || 0);
  const total = positive(flow.total) || 1;
  const k = (HEIGHT - NODE_GAP * 2) / total;
  const h = (v) => positive(v) * k;

  const fromVehicles = positive(flow.revenueVehicles - flow.costVehicles);
  const fromParts = positive(flow.revenueParts - flow.costParts);
  const gross = fromVehicles + fromParts;

  // Primeira coluna, centrada na altura da segunda
  const middleHeight = h(flow.costVehicles) + h(gross) + h(flow.costParts) + NODE_GAP * 2;
  const leftHeight = h(flow.revenueVehicles) + h(flow.revenueParts) + NODE_GAP;
  const leftTop = Math.max(0, (middleHeight - leftHeight) / 2);

  const vehicles = { y: leftTop, h: h(flow.revenueVehicles) };
  const parts = { y: vehicles.y + vehicles.h + NODE_GAP, h: h(flow.revenueParts) };
  const costV = { y: 0, h: h(flow.costVehicles) };
  const grossNode = { y: costV.y + costV.h + NODE_GAP, h: h(gross) };
  const costP = { y: grossNode.y + grossNode.h + NODE_GAP, h: h(flow.costParts) };

  // A margem bruta se abre em duas, com folga para os rótulos
  const expensesH = Math.min(h(flow.expenses), grossNode.h);
  const taxableH = Math.max(0, grossNode.h - expensesH);
  const outHeight = expensesH + taxableH + OUT_GAP;
  const outTop = Math.max(0, Math.min(HEIGHT - outHeight, grossNode.y + grossNode.h / 2 - outHeight / 2));
  const expenses = { y: outTop, h: expensesH };
  const taxable = { y: outTop + expensesH + OUT_GAP, h: taxableH };

  const credit = IOS.green;
  const taxed = IOS.red;
  const neutral = tokens.label3(theme);

  const ribbons = [
    { key: "v-cost", d: ribbon(X[0], vehicles.y, costV.h, X[1], costV.y, costV.h), fill: credit, o: 0.16 },
    { key: "v-gross", d: ribbon(X[0], vehicles.y + costV.h, h(fromVehicles), X[1], grossNode.y, h(fromVehicles)), fill: neutral, o: 0.32 },
    { key: "p-gross", d: ribbon(X[0], parts.y, h(fromParts), X[1], grossNode.y + h(fromVehicles), h(fromParts)), fill: neutral, o: 0.32 },
    { key: "p-cost", d: ribbon(X[0], parts.y + h(fromParts), costP.h, X[1], costP.y, costP.h), fill: credit, o: 0.16 },
    { key: "g-exp", d: ribbon(X[1], grossNode.y, expensesH, X[2], expenses.y, expensesH), fill: credit, o: 0.3 },
    { key: "g-tax", d: ribbon(X[1], grossNode.y + expensesH, taxableH, X[2], taxable.y, taxableH), fill: taxed, o: 0.42 },
  ];

  const nodes = [
    { key: "vehicles", col: 0, ...vehicles, color: neutral, label: "Receita de produtos com ST", value: flow.revenueVehicles },
    { key: "parts", col: 0, ...parts, color: neutral, label: "Receita de demais e serviços", value: flow.revenueParts },
    { key: "costV", col: 1, ...costV, color: credit, label: "COGS de produtos com ST", value: flow.costVehicles },
    { key: "gross", col: 1, ...grossNode, color: neutral, label: "Sobra depois do COGS", value: flow.grossMargin, before: true },
    { key: "costP", col: 1, ...costP, color: credit, label: "COGS de demais e serviços", value: flow.costParts },
    { key: "expenses", col: 2, ...expenses, color: credit, label: "Despesas elegíveis", value: flow.expenses },
    { key: "taxable", col: 2, ...taxable, color: taxed, label: "Margem tributável", value: flow.taxable, strong: true },
  ];

  const left = (col) => `${X[col] / 10}%`;

  return (
    <Wrap>
      <Canvas role="img" aria-label={`Da receita de ${formatCompact(flow.total)}, ${formatCompact(flow.costTotal)} de COGS e ${formatCompact(flow.expenses)} de despesas geram crédito, e ${formatCompact(flow.taxable)} são tributados`}>
        <svg viewBox={`0 0 1000 ${HEIGHT}`} preserveAspectRatio="none" aria-hidden="true">
          {ribbons.map((r) => <path key={r.key} d={r.d} fill={r.fill} fillOpacity={r.o} />)}
        </svg>
        {nodes.map((n) => <Node key={n.key} style={{ left: left(n.col), top: n.y, height: n.h, background: n.color }} />)}
        {nodes.map((n) => (
          <Label key={n.key} className={n.before ? "before" : ""} style={{ left: left(n.col), top: n.y + n.h / 2 }} title={formatCurrency(n.value)}>
            <b style={n.strong ? { color: taxed } : undefined}><Num value={n.value} format={formatCompact} /></b>
            {n.label}
          </Label>
        ))}
      </Canvas>
    </Wrap>
  );
}
